import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, ChevronDown, Mail, Instagram, Check, X } from "lucide-react";
import Footer from "../components/Footer";
import { useLanguage } from "../context/LanguageContext";
import LanguageSwitch from "../components/LanguageSwitch";
import { welcomeTranslations } from "../translations/welcomeTranslations";

// Refactored components
import {
  CoconSacreHero,
  CoconSacreWelcome,
  LangueTransmissionSection,
  EspaceModal,
  CoconeuseBioModal
} from "../components/cocon-sacre";
import EspaceNumeriqueModal from "../components/cocon-sacre/EspaceNumeriqueModal";
import { sanitizeHTML } from '../utils/sanitize';

const CoconSacre = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [openChristelle, setOpenChristelle] = useState(false);
  const [openAlexandra, setOpenAlexandra] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState(null);
  const [showEspaceModal, setShowEspaceModal] = useState(false);
  const [selectedCoconeuse, setSelectedCoconeuse] = useState(null);

  // Translation helper
  const t = (obj) => obj[language] || obj['fr'];
  const tr = welcomeTranslations;

  // FAQ items with coconeuse links handling
  const faqItems = tr.faqItems.map(item => ({
    question: t(item.question),
    answer: t(item.answer),
    hasButton: item.hasButton,
    buttonText: item.buttonText ? t(item.buttonText) : null,
    hasCoconeuses: item.hasCoconeuses,
    coconeuses: item.coconeuses,
    isLinkOnly: item.isLinkOnly,
    linkTo: item.linkTo,
    linkText: item.linkText ? t(item.linkText) : null
  }));

  // SEO meta tags
  useEffect(() => {
    document.title = t(tr.meta.title);
    
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", t(tr.meta.description));
    
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (language === 'en') {
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta');
        robotsMeta.name = 'robots';
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.content = 'noindex, nofollow';
    } else if (robotsMeta) {
      robotsMeta.content = 'index, follow';
    }
    
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", "https://immersion-passage.preview.emergentagent.com/cocon-sacre-2026");
    
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", t(tr.meta.title));
    
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute("content", t(tr.meta.description));
    
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute("content", "https://immersion-passage.preview.emergentagent.com/cocon-sacre-2026");
  }, [language]);

  return (
    <div className="min-h-screen bg-[#E8D2B5]">
      <LanguageSwitch />
      
      {/* Hero Section */}
      <CoconSacreHero />

      {/* Welcome Section */}
      <CoconSacreWelcome onOpenEspaceModal={() => setShowEspaceModal(true)} />

      {/* Ancrage poétique */}
      <section className="py-14 px-6 bg-[#516258]">
        <div className="max-w-xl mx-auto text-center space-y-3">
          <p className="font-serif text-base sm:text-lg text-[#F3E5CF]/70 leading-[2.2]">Ce n'est pas un voyage.</p>
          <p className="font-serif text-base sm:text-lg text-[#F3E5CF] italic leading-[2.2]">C'est un déplacement intérieur.</p>
        </div>
      </section>

      {/* Langue & Transmission */}
      <LangueTransmissionSection />

      {/* Presentation Block - PREMIUM rhythm */}
      <section className="py-10 px-6 bg-[#516258]">
        <div className="max-w-xl mx-auto text-center space-y-8">
          <div className="w-20 h-px bg-[#C5A059]/50 mx-auto" />
          <p className="text-[#F3E5CF]" style={{ lineHeight: '1.9', textWrap: 'balance' }} dangerouslySetInnerHTML={sanitizeHTML(t(tr.presentation.intro))} />
          <ul className="text-[#F3E5CF] text-sm space-y-2" style={{ lineHeight: '1.8' }}>
            {t(tr.presentation.list).map((item, idx) => (
              <li key={idx}>• {item}</li>
            ))}
          </ul>
          <p className="text-[#F3E5CF]/85 text-sm italic" style={{ lineHeight: '1.9', textWrap: 'balance' }} dangerouslySetInnerHTML={sanitizeHTML(t(tr.presentation.invitation))} />
          <div className="pt-6">
            <Link to="/cocon-sacre-2026/explorer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#C5A059] text-[#3F2A24] text-sm tracking-[0.15em] font-medium shadow-md hover:bg-[#3F2A24] hover:text-[#F3E5CF] transition-colors duration-300"
            >
              {t(tr.presentation.button)}
            </Link>
          </div>
        </div>
      </section>

      {/* Three Movements - PREMIUM spacing */}
      <section className="py-20 px-6 bg-[#F3E5CF]">
        <div className="max-w-5xl mx-auto">
          <div className="w-full h-px bg-[#3F2A24]/15 mb-14" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* NAGE */}
            <article>
              <div 
                className="w-full aspect-square rounded-2xl overflow-hidden mb-6 bg-cover bg-center shadow-sm"
                style={{ backgroundImage: `url(https://customer-assets.emergentagent.com/job_a4fdba0c-31a6-4b38-982d-6f7dfeee5b7e/artifacts/d2nuu11f_%20%E2%80%A2%20Nage%20%E2%80%A2%20-4.png)` }}
              />
              <div className="text-center px-2">
                <p 
                  className="text-[#3F2A24] text-sm font-medium mb-5 uppercase"
                  style={{ letterSpacing: '0.15em' }}
                >
                  {t(tr.movements.nage.location)}
                </p>
                <div className="space-y-4 text-[#3F2A24] text-sm" style={{ lineHeight: '1.8' }}>
                  <p style={{ textWrap: 'balance' }}>{t(tr.movements.nage.p1)}</p>
                  <p className="text-[#3F2A24]/75" style={{ textWrap: 'balance' }}>{t(tr.movements.nage.p2)}</p>
                </div>
              </div>
            </article>

            {/* PRIE */}
            <article>
              <div 
                className="w-full aspect-square rounded-2xl overflow-hidden mb-6 bg-cover bg-center shadow-sm"
                style={{ backgroundImage: "url('https://customer-assets.emergentagent.com/job_a4fdba0c-31a6-4b38-982d-6f7dfeee5b7e/artifacts/0titil0a_%E2%80%A2%20Prie%20%E2%80%A2.png')" }}
              />
              <div className="text-center px-2">
                <p 
                  className="text-[#3F2A24] text-sm font-medium mb-5 uppercase"
                  style={{ letterSpacing: '0.15em' }}
                >
                  {t(tr.movements.prie.location)}
                </p>
                <div className="space-y-4 text-[#3F2A24] text-sm" style={{ lineHeight: '1.8' }}>
                  <p style={{ textWrap: 'balance' }}>{t(tr.movements.prie.p1)}</p>
                  <p className="text-[#3F2A24]/75" style={{ textWrap: 'balance' }}>{t(tr.movements.prie.p2)}</p>
                </div>
              </div>
            </article>

            {/* RAYONNE */}
            <article>
              <div 
                className="w-full aspect-square rounded-2xl overflow-hidden mb-6 bg-cover bg-center shadow-sm"
                style={{ backgroundImage: `url(https://customer-assets.emergentagent.com/job_a4fdba0c-31a6-4b38-982d-6f7dfeee5b7e/artifacts/di4yegzx_%E2%80%A2%20Rayonne%20%E2%80%A2%20.png)` }}
              />
              <div className="text-center px-2">
                <p 
                  className="text-[#3F2A24] text-sm font-medium mb-5 uppercase"
                  style={{ letterSpacing: '0.15em' }}
                >
                  {t(tr.movements.rayonne.location)}
                </p>
                <div className="space-y-4 text-[#3F2A24] text-sm" style={{ lineHeight: '1.8' }}>
                  <p style={{ textWrap: 'balance' }}>{t(tr.movements.rayonne.p1)}</p>
                  <p className="text-[#3F2A24]/75" style={{ textWrap: 'balance' }}>{t(tr.movements.rayonne.p2)}</p>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Testimonials - PREMIUM typography */}
      <section className="py-24 px-6 bg-[#987f62]">
        <div className="max-w-3xl mx-auto space-y-14">
          <div className="text-center space-y-4">
            <p 
              className="text-[10px] uppercase text-[#F3E5CF]/70"
              style={{ letterSpacing: '0.4em' }}
            >
              {language === 'fr' ? 'TÉMOIGNAGES COCON' : 'COCON TESTIMONIALS'}
            </p>
            <h2 
              className="font-serif text-2xl md:text-3xl text-[#F3E5CF]"
              style={{ letterSpacing: '0.03em' }}
            >
              {t(tr.testimonials.title)}
            </h2>
            <p className="text-[#F3E5CF]/60 text-xs" style={{ letterSpacing: '0.1em' }}>
              {language === 'fr' ? 'Extraits des témoignages 2025' : 'Excerpts from 2025 testimonials'}
            </p>
          </div>
          <div className="space-y-5">
            {/* Christelle Testimonial */}
            <TestimonialCard 
              name="Christelle"
              quote="« Le chemin vers soi commence par un souffle, un espoir, une intention sacrée. »"
              isOpen={openChristelle}
              onToggle={() => setOpenChristelle(!openChristelle)}
              content={
                <>
                  <p>C'est par ces mots que Christelle décrit son expérience de Cocon Sacré – Bali 2025. Avant de partir, elle doutait. Voyager seule, si loin, lui semblait presque impossible. Et pourtant, quelque chose en elle a dit oui.</p>
                  <p>« Je me suis dit oui. Oui à moi. Oui à ce voyage intérieur. Et ce fut, sans aucun doute, la plus belle rencontre de ma vie. »</p>
                  <p>À Bali, Christelle a découvert une terre de douceur, de lenteur et de reliance.</p>
                  <p className="text-xs font-semibold uppercase" style={{ letterSpacing: '0.15em' }}>CHRISTELLE K. – PARTICIPANTE À LA RETRAITE COCON SACRÉ – BALI 2025</p>
                </>
              }
              response="Christelle, ton oui a ouvert bien plus qu'un voyage. Il a ouvert un passage. Merci pour la douceur que tu as offerte au cercle."
            />
            {/* Alexandra Testimonial */}
            <TestimonialCard 
              name="Alexandra"
              quote="« Cette retraite a transformé ma vie. »"
              isOpen={openAlexandra}
              onToggle={() => setOpenAlexandra(!openAlexandra)}
              content={
                <>
                  <p>Dès son arrivée à Bali, Alexandra a ressenti une paix profonde.</p>
                  <p>« Chaque jour me rapprochait un peu plus de moi-même. Les séances de yoga au lever du soleil, les soins sonores, les cérémonies balinaises… »</p>
                  <p className="text-xs font-semibold uppercase" style={{ letterSpacing: '0.15em' }}>ALEXANDRA W. – PARTICIPANTE À LA RETRAITE HOLISTIQUE COCON SACRÉ – BALI 2025</p>
                </>
              }
              response="Alexandra, ta présence a été un souffle calme et profond pour le cercle. Merci pour ta confiance."
            />
          </div>
        </div>
      </section>

      {/* FAQ Section - PREMIUM spacing & alignment - INVERTED COLORS */}
      <section id="faq" className="py-24 px-6 bg-[#6d6049]">
        <div className="max-w-2xl mx-auto space-y-12">
          <div className="text-center">
            <h2 
              className="font-serif text-2xl md:text-3xl text-[#F3E5CF] mb-3"
              style={{ letterSpacing: '0.03em' }}
            >
              {t(tr.faq.title)}
            </h2>
            <p 
              className="text-[#F3E5CF]/60 text-xs uppercase"
              style={{ letterSpacing: '0.2em' }}
            >
              {t(tr.faq.subtitle)}
            </p>
          </div>
          <div className="space-y-2.5">
            {faqItems.map((item, index) => {
              // Handle link-only FAQ items (like Jeinar)
              if (item.isLinkOnly) {
                return (
                  <div key={index} className="rounded-xl overflow-hidden">
                    <Link
                      to={`${item.linkTo}${item.linkTo.includes('?') ? '&' : '?'}lang=${language}`}
                      className="w-full px-6 py-5 flex justify-between items-center text-left bg-[#F3E5CF]/20 text-[#F3E5CF] hover:bg-[#F3E5CF]/30 transition-colors group"
                      data-testid={`faq-link-${index}`}
                    >
                      <span className="font-medium text-base pr-6" style={{ lineHeight: '1.5' }}>{item.question.replace(/ ([?!:;])/g, '\u00A0$1')}</span>
                      <ArrowRight className="w-4 h-4 flex-shrink-0 opacity-60 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  </div>
                );
              }

              // Process answer to replace {{COCONEUSE_LINKS}} with clickable names
              let processedAnswer = item.answer;
              if (item.hasCoconeuses && item.coconeuses) {
                const coconeusesLinks = item.coconeuses.map(name => {
                  const displayName = name.charAt(0).toUpperCase() + name.slice(1);
                  const formattedName = displayName === 'Stephanie' ? 'Stéphanie' : displayName === 'Valerie' ? 'Valérie' : displayName;
                  // Jeinar links to her dedicated page, others open modal
                  if (name === 'jeinar') {
                    return `<a href="/story/jeinar?lang=${language}" class="coconeuse-link" data-coconeuse="${name}">${formattedName} <span class="arrow">→</span></a>`;
                  }
                  return `<button class="coconeuse-link" data-coconeuse="${name}">${formattedName} <span class="arrow">→</span></button>`;
                }).join(' · ');
                processedAnswer = processedAnswer.replace('{{COCONEUSE_LINKS}}', coconeusesLinks);
              }

              return (
                <div key={index} className="rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className={`w-full px-6 py-5 flex justify-between items-center text-left transition-colors ${
                      openFaq === index 
                        ? 'bg-[#F3E5CF] text-[#3F2A24]' 
                        : 'bg-[#F3E5CF]/20 text-[#F3E5CF] hover:bg-[#F3E5CF]/30'
                    }`}
                  >
                    <span className="font-medium text-base pr-6" style={{ lineHeight: '1.5' }}>{item.question.replace(/ ([?!:;])/g, '\u00A0$1')}</span>
                    <ChevronDown className={`w-4 h-4 flex-shrink-0 opacity-60 transition-transform duration-300 ${openFaq === index ? "rotate-180" : ""}`} />
                  </button>
                  {openFaq === index && (
                    <div 
                      className="px-6 pb-6 pt-4 bg-[#F3E5CF] text-[#3F2A24]/80 text-sm" 
                      style={{ lineHeight: '1.85' }}
                    >
                      <div 
                        className="whitespace-pre-line [&_.coconeuse-link]:text-[#C5A059] [&_.coconeuse-link]:font-medium [&_.coconeuse-link]:underline [&_.coconeuse-link]:underline-offset-2 [&_.coconeuse-link]:cursor-pointer [&_.coconeuse-link]:hover:text-[#3F2A24] [&_.coconeuse-link]:transition-colors [&_.coconeuse-link_.arrow]:opacity-40 [&_.coconeuse-link_.arrow]:ml-0.5 [&_.coconeuse-link:hover_.arrow]:opacity-100 [&_.coconeuse-link_.arrow]:transition-opacity"
                        dangerouslySetInnerHTML={sanitizeHTML(processedAnswer)}
                        onClick={(e) => {
                          // Handle clicks on coconeuse links (except Jeinar which is an <a> tag)
                          if (e.target.classList.contains('coconeuse-link') || e.target.closest('.coconeuse-link')) {
                            const element = e.target.classList.contains('coconeuse-link') ? e.target : e.target.closest('.coconeuse-link');
                            const coconeuseName = element.getAttribute('data-coconeuse');
                            // Jeinar is handled by native <a> navigation
                            if (coconeuseName && coconeuseName !== 'jeinar') {
                              e.preventDefault();
                              setSelectedCoconeuse(coconeuseName);
                            }
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Coconeuse Bio Modal */}
      <CoconeuseBioModal 
        coconeuse={selectedCoconeuse}
        isOpen={!!selectedCoconeuse}
        onClose={() => setSelectedCoconeuse(null)}
      />

      {/* Digital Space Section */}
      <DigitalSpaceSection onOpenModal={() => setShowEspaceModal(true)} t={t} tr={tr} />

      {/* Explore CTA */}
      <section className="py-16 px-6 bg-[#E8D2B5]">
        <div className="max-w-2xl mx-auto text-center">
          <Link
            to="/cocon-sacre-2026/explorer"
            className="inline-block px-10 py-5 bg-[#C5A059] text-[#3F2A24] text-base md:text-lg tracking-[0.08em] font-medium rounded-full shadow-lg hover:bg-[#3F2A24] hover:text-[#F3E5CF] transition-all duration-300"
            data-testid="explore-cocon-sacre-btn"
          >
            {t(tr.exploreButton)}
          </Link>
        </div>
      </section>

      {/* Newsletter Section */}
      <NewsletterSection 
        newsletterEmail={newsletterEmail}
        setNewsletterEmail={setNewsletterEmail}
        newsletterStatus={newsletterStatus}
        setNewsletterStatus={setNewsletterStatus}
        t={t}
        tr={tr}
        language={language}
      />

      <Footer />

      {/* Modal - Mon Espace Numérique */}
      {showEspaceModal && (
        <EspaceNumeriqueModal 
          isOpen={showEspaceModal} 
          onClose={() => setShowEspaceModal(false)} 
          language={language}
        />
      )}
    </div>
  );
};

// Testimonial Card Component - PREMIUM finish
const TestimonialCard = ({ name, quote, isOpen, onToggle, content, response }) => (
  <article className="bg-[#c0a084] rounded-2xl px-7 py-6 shadow-sm">
    <button type="button" onClick={onToggle} className="w-full flex items-center justify-between text-left">
      <div className="pr-6">
        <h3 
          className="font-serif text-lg md:text-xl text-[#3F2A24]"
          style={{ letterSpacing: '0.02em' }}
        >
          {name}
        </h3>
        <p 
          className="italic text-[#3F2A24]/85 text-sm mt-3" 
          style={{ lineHeight: '1.7' }}
        >
          {quote}
        </p>
      </div>
      <ChevronDown className={`w-4 h-4 flex-shrink-0 text-[#3F2A24]/60 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
    </button>
    {isOpen && (
      <div 
        className="mt-6 pt-6 border-t border-[#3F2A24]/15 space-y-5 text-[#3F2A24] text-sm" 
        style={{ lineHeight: '1.85' }}
      >
        {content}
        <div className="pt-5">
          <p className="font-semibold text-[#3F2A24] mb-2 text-xs uppercase" style={{ letterSpacing: '0.1em' }}>Réponse de Jeinar</p>
          <p className="italic text-[#3F2A24]/90">{response}</p>
        </div>
      </div>
    )}
  </article>
);

// Digital Space Section Component
const DigitalSpaceSection = ({ onOpenModal, t, tr }) => {
  const spaces = [
    { title: t(tr.digitalSpace.preCocon.title), description: t(tr.digitalSpace.preCocon.description) },
    { title: t(tr.digitalSpace.cocon.title), description: t(tr.digitalSpace.cocon.description) },
    { title: t(tr.digitalSpace.postCocon.title), description: t(tr.digitalSpace.postCocon.description) }
  ];

  return (
    <section className="py-20 px-6 mt-0 bg-[#E8D2B5]">
      <div className="max-w-2xl mx-auto">
        <p className="text-center text-[#3F2A24] leading-relaxed" dangerouslySetInnerHTML={sanitizeHTML(t(tr.digitalSpace.intro))} />
        <div className="mt-12 mb-8 flex justify-center">
          <button onClick={onOpenModal} className="ml-onclick-form inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#C5A059] text-[#3F2A24] text-[0.75rem] tracking-[0.22em] uppercase shadow-sm hover:bg-[#987f62] hover:text-[#F3E5CF] transition-colors" data-testid="enter-space-btn">
            <span>{t(tr.digitalSpace.enterButton)}</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-10 mt-10">
          {spaces.map((space, i) => (
            <div key={i}>
              <p className="text-[#C5A059] text-sm tracking-[0.35em] uppercase text-center">⸻</p>
              <div className="text-center space-y-3 mt-4">
                <p className="text-[#3F2A24] font-serif text-xl uppercase tracking-[0.25em]">{space.title}</p>
                <p className="text-[#3F2A24] leading-relaxed">{space.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Newsletter Section Component
const NewsletterSection = ({ newsletterEmail, setNewsletterEmail, newsletterStatus, setNewsletterStatus, t, tr, language }) => (
  <section className="py-12 px-6 bg-[#E8D2B5]">
    <div className="max-w-2xl mx-auto text-center space-y-4">
      <h3 className="text-xs md:text-sm tracking-[0.35em] uppercase font-medium text-[#3F2A24]">{t(tr.newsletter.title)}</h3>
      <p className="text-[#3F2A24] text-sm leading-relaxed" dangerouslySetInnerHTML={sanitizeHTML(t(tr.newsletter.description))} />
      <div className="mt-4 max-w-sm mx-auto">
        {newsletterStatus === 'success' ? (
          <div className="flex items-center justify-center gap-2 py-4 px-6 bg-[#3F2A24] rounded-full text-[#F3E5CF]">
            <Check className="w-5 h-5" />
            <span className="text-sm tracking-wide">{t(tr.newsletter.success)}</span>
          </div>
        ) : (
          <form onSubmit={(e) => {
            e.preventDefault();
            if (!newsletterEmail) return;
            setNewsletterStatus('loading');
            setTimeout(() => setNewsletterStatus('success'), 1000);
          }} className="space-y-2">
            <input type="email" value={newsletterEmail} onChange={(e) => setNewsletterEmail(e.target.value)} placeholder={t(tr.newsletter.placeholder)} required className="w-full bg-white text-[#3F2A24] border border-[#C5A059] rounded-full text-sm py-3 px-5 text-center focus:outline-none focus:border-[#3F2A24]" />
            <button type="submit" disabled={newsletterStatus === 'loading'} className="w-full bg-[#3F2A24] text-[#F3E5CF] rounded-full text-xs tracking-[0.15em] uppercase py-3 px-6 hover:bg-[#6E3A1E] transition-colors disabled:opacity-50">
              {newsletterStatus === 'loading' ? (language === 'fr' ? 'Envoi...' : 'Sending...') : t(tr.newsletter.button)}
            </button>
          </form>
        )}
      </div>
      <div className="mt-3">
        <a href="https://www.instagram.com/exquisite_impactful_retreats/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#3F2A24] text-[#F3E5CF] text-xs tracking-[0.18em] uppercase shadow-sm hover:bg-[#6E3A1E] transition-colors">
          <Instagram className="w-4 h-4" />
          <span>Instagram</span>
        </a>
      </div>
    </div>
  </section>
);

export default CoconSacre;
